import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/site/Reveal";
import SectionHead from "@/components/site/SectionHead";
import CTASection from "@/components/site/CTASection";
import { useLang } from "@/i18n";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Servizi = () => {
  const { d } = useLang();
  const p = d.services;
  return (
    <div data-testid="servizi-page">
      <section className="bg-paper py-20 lg:py-28" data-testid="servizi-hero">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent"
          >
            <span className="h-px w-10 bg-accent" /> {p.heroLabel}
          </motion.p>
          <h1 className="max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="servizi-headline">
            <MaskedLine delay={0.1}>{p.heroL1}</MaskedLine>
            <MaskedLine delay={0.25}><span className="italic text-accent">{p.heroL2}</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-mutedink sm:text-lg"
          >
            {p.sub}
          </motion.p>
        </div>
      </section>

      {p.categories.map((cat, ci) => (
        <section
          key={cat.id}
          className={`py-24 lg:py-32 ${ci % 2 === 0 ? "border-t border-ink/10 bg-white" : "bg-paper"}`}
          data-testid={`servizi-cat-${cat.id}`}
        >
          <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
            <SectionHead overline={`${String(ci + 1).padStart(2, "0")} — ${cat.title}`} title={cat.title} />
            <div className="mt-14 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
              {cat.items.map((s, i) => (
                <Reveal
                  key={s.name}
                  delay={i * 0.06}
                  className="flex flex-col rounded-md border border-ink/12 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent"
                  data-testid={`servizio-${slug(s.name)}`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-serif text-2xl text-ink">{s.name}</h3>
                    <p className="whitespace-nowrap font-serif text-xl text-accent" data-testid={`servizio-${slug(s.name)}-price`}>
                      {s.price} <span className="font-sans text-xs font-normal text-mutedink">{p.vat}</span>
                    </p>
                  </div>
                  {s.alt && (
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-mutedink" data-testid={`servizio-${slug(s.name)}-alt`}>{s.alt}</p>
                  )}
                  <p className="mt-4 text-sm leading-relaxed text-mutedink">{s.desc}</p>
                  <p className="mt-4 text-sm leading-relaxed text-ink/85">
                    <span className="font-semibold">{p.idealLabel}: </span>
                    {s.ideal}
                  </p>
                  {s.includes && (
                    <div className="mt-5 border-t border-ink/10 pt-5">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">{p.includesLabel}</p>
                      <ul className="mt-3 space-y-2 text-sm">
                        {s.includes.map((f) => (
                          <li key={f} className="flex gap-3">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={2} />
                            <span className="text-ink/85">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {!s.includes && (
                    <p className="mt-5 border-t border-ink/10 pt-5 text-xs leading-relaxed text-mutedink">{p.scopeNote}</p>
                  )}
                  {s.note && (
                    <p className="mt-4 border-l-2 border-accent pl-4 text-xs leading-relaxed text-mutedink" data-testid={`servizio-${slug(s.name)}-note`}>{s.note}</p>
                  )}
                  {s.link && (
                    <Link
                      to={s.link.href}
                      data-testid={`servizio-${slug(s.name)}-link`}
                      className="mt-3 inline-flex items-center gap-2 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-accent transition-colors hover:text-accent-dark"
                    >
                      {s.link.label} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                  <Link
                    to={`/contatti?area=servizio-specifico`}
                    data-testid={`servizio-${slug(s.name)}-cta`}
                    className="mt-6 inline-flex items-center justify-center gap-3 self-start rounded-md bg-accent px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
                  >
                    {p.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="hero-grain relative bg-ink py-24 text-paper lg:py-32" data-testid="servizi-checkup-callout">
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-8">
              <Reveal>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent-bright">{p.checkupCallout.label}</p>
                <h2 className="mt-4 font-serif text-3xl leading-tight sm:text-4xl" data-testid="servizi-checkup-title">{p.checkupCallout.title}</h2>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/65">{p.checkupCallout.body}</p>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-4">
              <div className="rounded-md border border-white/15 p-8" data-testid="servizi-checkup-card">
                <p className="font-serif text-4xl text-accent-bright">{p.checkupCallout.price}</p>
                <Link
                  to="/check-up"
                  data-testid="servizi-checkup-cta"
                  className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-md bg-accent px-7 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
                >
                  {p.checkupCallout.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Servizi;
